import React, { useMemo, useRef, useState } from 'react';
import styles from './styles.module.css';
import { FiTrash2 } from 'react-icons/fi';
import { TbFileDownload } from 'react-icons/tb';
import { FaFileExcel } from 'react-icons/fa';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import { SET_POSITIONS } from '../../../actions/types';
import { useDispatch, useSelector } from 'react-redux';
import { createNotification } from '../../../components/Notifications/Notifications';
import Confirmation from '../../../components/Confirmation';
import {
  addKeyword,
  deleteKeyword,
  getKeywords,
  getPositionDataAll,
  getPositionDataSingle,
  postExcelKeywords,
  downloadExcelKeywords
} from '../../../actions/positions';

const Index = ({
  selectedSite,
  dateFrom,
  dateTo,
  provider,
  viewAllKeywords,
  setSelectedKeyword,
  setViewAllKeywords,
  selectedKeyword,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [newKeyword, setNewKeyword] = useState('');
  const { keywords = [], hints = [] } = useSelector((state) => state.positions || {});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleAddKeyword = async () => {
    if (!selectedSite) return createNotification('error', 'Выберите сайт');
    if (!newKeyword.trim()) return createNotification('error', 'Введите ключевое слово');

    const normalizedValue = newKeyword.trim().toLowerCase();
    const matchedHint = hints.find((h) => h?.toLowerCase() === normalizedValue);
    const alreadyExists = keywords.some((k) => k.keyword?.toLowerCase() === normalizedValue);

    if (!matchedHint) {
      return createNotification('error', 'Выберите ключевое слово из списка');
    }

    if (alreadyExists) {
      return createNotification('error', 'Ключевое слово уже добавлено');
    }

    const res = await dispatch(addKeyword(selectedSite, matchedHint.trim()));
    if (res) {
      setNewKeyword('');
      setIsDropdownOpen(false);
      dispatch(getKeywords(selectedSite));
      if (viewAllKeywords) {
        await dispatch(getPositionDataAll(selectedSite, dateFrom, dateTo, provider));
      }
    }
  };

  const handleSelectKeyword = async (keyword) => {
    if (!selectedSite) return;
    setViewAllKeywords(false);
    setSelectedKeyword(keyword);
    await dispatch(getPositionDataSingle(selectedSite, dateFrom, dateTo, provider, keyword.id));
  };

  const handleDeleteKeyword = async (id) => {
    if (!selectedSite) return;
    await dispatch(deleteKeyword(selectedSite, id));
    dispatch(getKeywords(selectedSite));
    if (selectedKeyword?.id === id) setSelectedKeyword(null);

    // обновим all при включенном режиме
    if (viewAllKeywords) {
      await dispatch(getPositionDataAll(selectedSite, dateFrom, dateTo, provider));
    }

    setConfirmDelete(null);
  };

  const handleConfirmDelete = (keyword) => {
    setConfirmDelete(keyword);
  };

  const filteredHints = useMemo(() => {
    const search = newKeyword.trim().toLowerCase();
    const used = new Set(keywords.map((k) => k.keyword?.toLowerCase()));

    return hints
      .filter(Boolean)
      .map((h) => h.toString())
      .filter((h) => !used.has(h.toLowerCase()))
      .filter((h) => !search || h.toLowerCase().includes(search))
      .slice(0, 30);
  }, [hints, keywords, newKeyword]);

  const handleSelectHint = (hint) => {
    setNewKeyword(hint);
    setIsDropdownOpen(false);
  };

  const handleAddExcel = async () => {
    if (!selectedSite) return createNotification('error', 'Выберите сайт');
    fileInputRef.current?.click();
  };

  const handleDownloadExcel = async () => {
    if (!selectedSite) return createNotification('error', 'Выберите сайт');
    const res = await dispatch(downloadExcelKeywords(selectedSite));

    if (res) {
      createNotification('success', 'Файл Excel скачан');
    }
  };

  const handleExcelChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      e.target.value = '';
      return;
    }

    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      createNotification('error', 'Допустимы файлы .xls/.xlsx/.csv');
      e.target.value = '';
      return;
    }

    const res = await dispatch(postExcelKeywords(selectedSite, file));

    if (res) {
      await dispatch(getKeywords(selectedSite));
      if (viewAllKeywords) {
        await dispatch(getPositionDataAll(selectedSite, dateFrom, dateTo, provider));
      }
    }

    // всегда очищаем input, чтобы можно было выбрать тот же файл повторно
    e.target.value = '';
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <h3 className={styles.title}>Ключевые слова</h3>
        <label className={styles.checkboxLabel}>
          Все
          <input
            type="checkbox"
            checked={viewAllKeywords}
            onChange={async (e) => {
              const checked = e.target.checked;
              setViewAllKeywords(checked);
              setSelectedKeyword(null);

              if (checked) {
                await dispatch(getPositionDataAll(selectedSite, dateFrom, dateTo, provider));
              } else {
                dispatch({ type: SET_POSITIONS, payload: [] });
              }
            }}
          />
        
        </label>
      </div>

      <div className={styles.addKeyword}>
        <div className={styles.comboWrapper}>
          <Input
            placeholder="Ключевые слова..."
            value={newKeyword}
            onFocus={() => setIsDropdownOpen(true)}
            onBlur={() => setTimeout(() => setIsDropdownOpen(false), 150)}
            onChange={(e) => {
              setNewKeyword(e.target.value);
              setIsDropdownOpen(true);
            }}
            autoComplete="off"
          />
          {isDropdownOpen && (
            <div className={styles.hintsDropdown}>
              {filteredHints.length ? (
                filteredHints.map((hint) => (
                  <button
                    type="button"
                    key={hint}
                    className={styles.hintItem}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectHint(hint);
                    }}
                  >
                    {hint}
                  </button>
                ))
              ) : (
                <div className={styles.noHints}>Нет доступных вариантов</div>
              )}
            </div>
          )}
        </div>
        <Button mr="0 0 0 0" text="+" onClick={handleAddKeyword} />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          style={{ display: 'none' }}
          onChange={handleExcelChange}
        />
        <Button mr="0 0 0 0" icon={FaFileExcel} onClick={handleAddExcel} />
        <Button mr="0 0 0 0" icon={TbFileDownload} onClick={handleDownloadExcel} />
      </div>

      <div className={styles.keywordList}>
        {keywords.length ? (
          keywords.map((k) => (
            <div
              key={k.id}
              className={`${styles.keywordItem} ${
                selectedKeyword?.id === k.id ? styles.active : ''
              }`}
              onClick={() => handleSelectKeyword(k)}
            >
              <span>{k.keyword}</span>
              <button
                className={styles.deleteBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmDelete(k);
                }}
              >
                <FiTrash2 />
              </button>
            </div>
          ))
        ) : (
          <div className={styles.empty}>Нет ключевых слов</div>
        )}
      </div>

      {/* === Подтверждение удаления === */}
      {confirmDelete && (
        <Confirmation
          absolute
          title="Удалить ключевое слово?"
          confirmation={confirmDelete.keyword}
          color="#222"
          acceptConfirmation={() => handleDeleteKeyword(confirmDelete.id)}
          rejectConfirmation={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default Index;
